import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    styled,
    alpha
} from "@mui/material";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem, treeItemClasses } from "@mui/x-tree-view/TreeItem";
import {
    Folder as FolderIcon,
    FolderOpen as FolderOpenIcon,
    Storage as RepoIcon,
    ChevronRight as ChevronRightIcon,
    ExpandMore as ExpandMoreIcon,
    Download as DownloadIcon
} from "@mui/icons-material";
import { useAuth } from "@clerk/clerk-react";
import {
    getGitRepositoryApi,
    getGitDirectoriesApi,
    getGitFilesApi
} from "@/api/api";
import { getFileIcon, FILE_ICON_SIZE } from "@/utils/fileIcons";
import type { PeerState } from "@/collab/usePresence";

// ── VS Code-style tree item ────────────────────────────────────────────
const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
    // Each row
    [`& .${treeItemClasses.content}`]: {
        padding: "1px 8px",
        minHeight: 22,
        borderRadius: 0,
        gap: 0,
        "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08)
        },
        "&.Mui-selected": {
            backgroundColor: alpha(theme.palette.primary.main, 0.14),
            "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.18)
            }
        },
        "&.Mui-focused": {
            backgroundColor: "transparent"
        },
        "&.Mui-selected.Mui-focused": {
            backgroundColor: alpha(theme.palette.primary.main, 0.14)
        }
    },

    // Expand / collapse chevron
    [`& .${treeItemClasses.iconContainer}`]: {
        width: 16,
        minWidth: 16,
        marginRight: 0,
        "& svg": {
            fontSize: 16
        }
    },

    // Label text
    [`& .${treeItemClasses.label}`]: {
        fontSize: 13,
        lineHeight: "22px",
        fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
        paddingLeft: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },

    // Indentation guide – subtle vertical line for each nesting level
    [`& .${treeItemClasses.groupTransition}`]: {
        marginLeft: 10,
        paddingLeft: 8,
        borderLeft: `1px solid ${alpha(theme.palette.divider, 0.4)}`
    }
}));

export interface FileTreeNode {
    id: string;
    label: string;
    type: "repository" | "directory" | "file";
    fileId?: number;
    path?: string;
    language?: string;
    children?: FileTreeNode[];
}

interface WorkProductEditorFileListProps {
    repositoryId: number;
    onFileSelect: (fileId: number, filePath: string) => void;
    onFileDownload: (fileId: number, filePath: string) => void;
    presencePeers?: PeerState[];
    /** Prefer this generated artifact path when selecting the initial file. */
    initialFilePath?: string;
    /** Changes whenever a newly opened work product should trigger automatic selection. */
    autoSelectKey?: string | number;
}

export interface InitialFileSelection {
    file: FileTreeNode;
    ancestorIds: string[];
}

const normalizePath = (value: string): string => value
    .replace(/\\/g, "/")
    .replace(/^\.\//, "")
    .replace(/^\//, "")
    .toLowerCase();

/** Finds a preferred artifact file, falling back to the first file in tree order. */
export function findInitialFileSelection(
    tree: FileTreeNode,
    preferredPath?: string
): InitialFileSelection | null {
    const candidates: InitialFileSelection[] = [];
    const visit = (node: FileTreeNode, ancestorIds: string[]) => {
        if (node.type === "file" && node.fileId != null && node.path) {
            candidates.push({ file: node, ancestorIds });
        }
        node.children?.forEach(child => visit(child, [...ancestorIds, node.id]));
    };
    visit(tree, []);
    if (candidates.length === 0) return null;

    const normalizedPreferredPath = normalizePath(preferredPath || "");
    if (normalizedPreferredPath) {
        const preferredName = normalizedPreferredPath.split("/").pop();
        const exact = candidates.find(candidate => {
            const candidatePath = normalizePath(candidate.file.path || "");
            return candidatePath === normalizedPreferredPath || candidatePath.endsWith(`/${normalizedPreferredPath}`);
        });
        if (exact) return exact;

        const byFilename = candidates.find(candidate => normalizePath(candidate.file.label) === preferredName);
        if (byFilename) return byFilename;
    }

    return candidates[0];
}

const WorkProductEditorFileList: React.FC<WorkProductEditorFileListProps> = ({
    repositoryId,
    onFileSelect,
    onFileDownload,
    presencePeers = [],
    initialFilePath,
    autoSelectKey
}) => {
    const { getToken } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [treeData, setTreeData] = useState<FileTreeNode | null>(null);
    const [expandedItems, setExpandedItems] = useState<string[]>([]);
    const [selectedItem, setSelectedItem] = useState<string | null>(null);
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; file: FileTreeNode } | null>(null);
    const automaticSelectionRef = useRef<string | null>(null);
    const onFileSelectRef = useRef(onFileSelect);

    useEffect(() => {
        onFileSelectRef.current = onFileSelect;
    }, [onFileSelect]);

    useEffect(() => {
        automaticSelectionRef.current = null;
        setSelectedItem(null);
        setTreeData(null);
        loadRepositoryTree();
    }, [repositoryId, autoSelectKey]);

    useEffect(() => {
        if (!treeData) return;
        const selectionKey = `${repositoryId}:${String(autoSelectKey ?? "default")}:${initialFilePath || ""}`;
        if (automaticSelectionRef.current === selectionKey) return;

        const selection = findInitialFileSelection(treeData, initialFilePath);
        if (!selection?.file.fileId || !selection.file.path) return;

        automaticSelectionRef.current = selectionKey;
        setSelectedItem(selection.file.id);
        setExpandedItems(current => Array.from(new Set([...current, ...selection.ancestorIds])));
        onFileSelectRef.current(selection.file.fileId, selection.file.path);
    }, [autoSelectKey, initialFilePath, repositoryId, treeData]);

    const loadRepositoryTree = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = await getToken();

            if (!token) {
                throw new Error("Authentication token not available");
            }

            // Fetch repository details
            const repo = await getGitRepositoryApi(repositoryId, token);

            // Fetch directories and files
            const [directories, files] = await Promise.all([
                getGitDirectoriesApi(repositoryId, token),
                getGitFilesApi(repositoryId, token)
            ]);

            // Build the tree structure
            const tree = buildTree(repo, directories, files);
            setTreeData(tree);
            setExpandedItems([tree.id]); // Auto-expand root
        } catch (err: any) {
            console.error("Error loading repository tree:", err);
            setError(err.message || "Failed to load repository tree");
        } finally {
            setLoading(false);
        }
    };

    const buildTree = (repo: any, directories: any[], files: any[]): FileTreeNode => {
        // Map directory IDs to tree nodes
        const dirMap = new Map<number, FileTreeNode>();
        const rootNode: FileTreeNode = {
            id: `repo-${repo.id}`,
            label: repo.name,
            type: "repository",
            children: []
        };

        // Build directory nodes using database IDs
        directories.forEach(dir => {
            const label = dir.name || dir.path?.split("/").pop() || "";
            if (!label) return;

            const node: FileTreeNode = {
                id: `dir-${dir.id}`,
                label,
                type: "directory",
                path: dir.path,
                children: []
            };
            dirMap.set(dir.id, node);
        });

        // Link directories to their parents using parent_id
        directories.forEach(dir => {
            const node = dirMap.get(dir.id);
            if (!node) return;

            if (dir.parent_id) {
                const parentNode = dirMap.get(dir.parent_id);
                if (parentNode) {
                    parentNode.children!.push(node);
                } else {
                    rootNode.children!.push(node);
                }
            } else {
                rootNode.children!.push(node);
            }
        });

        // Add files to their directories using directory_id
        files.forEach(file => {
            const fileNode: FileTreeNode = {
                id: `file-${file.id}`,
                label: file.filename || file.path?.split("/").pop() || file.path,
                type: "file",
                fileId: file.id,
                path: file.path,
                language: file.language
            };

            if (file.directory_id) {
                const parentDir = dirMap.get(file.directory_id);
                if (parentDir) {
                    parentDir.children!.push(fileNode);
                } else {
                    rootNode.children!.push(fileNode);
                }
            } else {
                rootNode.children!.push(fileNode);
            }
        });

        // Sort children (directories first, then files, alphabetically)
        const sortChildren = (node: FileTreeNode) => {
            if (node.children) {
                node.children.sort((a, b) => {
                    if (a.type === b.type) {
                        return a.label.localeCompare(b.label);
                    }
                    return a.type === "directory" ? -1 : 1;
                });
                node.children.forEach(sortChildren);
            }
        };
        sortChildren(rootNode);

        return rootNode;
    };

    const renderNodeIcon = (node: FileTreeNode, isExpanded: boolean) => {
        if (node.type === "repository") {
            return <RepoIcon sx={{ fontSize: FILE_ICON_SIZE, color: "#858585", flexShrink: 0 }} />;
        }

        if (node.type === "directory") {
            return isExpanded ? (
                <FolderOpenIcon sx={{ fontSize: FILE_ICON_SIZE, color: "#dcb67a", flexShrink: 0 }} />
            ) : (
                <FolderIcon sx={{ fontSize: FILE_ICON_SIZE, color: "#dcb67a", flexShrink: 0 }} />
            );
        }

        // File — resolve icon by filename
        return getFileIcon(node.label, FILE_ICON_SIZE);
    };

    const renderTree = (node: FileTreeNode) => {
        const isExpanded = expandedItems.includes(node.id);
        const icon = renderNodeIcon(node, isExpanded);

        return (
            <StyledTreeItem
                key={node.id}
                itemId={node.id}
                label={
                    <Box
                        onContextMenu={(event) => {
                            if (node.type !== "file" || !node.fileId || !node.path) return;
                            event.preventDefault();
                            event.stopPropagation();
                            setSelectedItem(node.id);
                            setContextMenu({ mouseX: event.clientX + 2, mouseY: event.clientY - 6, file: node });
                        }}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            minHeight: 22
                        }}
                    >
                        {icon}
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 13,
                                lineHeight: "22px",
                                fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}
                        >
                            {node.label}
                        </Typography>
                        {node.type === "file" && presencePeers
                            .filter(peer => peer.activity?.target.kind === "file" && peer.activity.target.id === String(node.fileId))
                            .slice(0, 3)
                            .map(peer => (
                                <Box
                                    key={`${peer.user!.userid}:${node.fileId}`}
                                    aria-label={`${peer.user!.name} is ${peer.activity!.activity}`}
                                    title={`${peer.user!.name} is ${peer.activity!.activity}`}
                                    sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: peer.user!.color, flexShrink: 0 }}
                                />
                            ))}
                    </Box>
                }
            >
                {node.children && node.children.length > 0
                    ? node.children.map(child => renderTree(child))
                    : null}
            </StyledTreeItem>
        );
    };

    const handleSelectedItemsChange = (
        event: React.SyntheticEvent,
        itemId: string | null
    ) => {
        setSelectedItem(itemId);

        // If a file is selected, fire the callback
        if (itemId && itemId.startsWith("file-")) {
            const findNode = (node: FileTreeNode): FileTreeNode | null => {
                if (node.id === itemId) return node;
                if (node.children) {
                    for (const child of node.children) {
                        const found = findNode(child);
                        if (found) return found;
                    }
                }
                return null;
            };

            if (treeData) {
                const fileNode = findNode(treeData);
                if (fileNode && fileNode.fileId && fileNode.path) {
                    onFileSelect(fileNode.fileId, fileNode.path);
                }
            }
        }
    };

    const handleExpandedItemsChange = (
        event: React.SyntheticEvent,
        itemIds: string[]
    ) => {
        setExpandedItems(itemIds);
    };

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
                minHeight={200}
            >
                <CircularProgress size={24} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography color="error" variant="body2">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (!treeData) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                    No repository data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                height: "100%",
                overflow: "auto",
                fontFamily: "'Segoe UI', 'Helvetica Neue', sans-serif",
                fontSize: 13,
                userSelect: "none",
                // Slim scrollbar matching VS Code
                "&::-webkit-scrollbar": { width: 6 },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.15)",
                    borderRadius: 3
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent"
                }
            }}
        >
            <SimpleTreeView
                expandedItems={expandedItems}
                selectedItems={selectedItem}
                onSelectedItemsChange={handleSelectedItemsChange}
                onExpandedItemsChange={handleExpandedItemsChange}
                slots={{
                    collapseIcon: ExpandMoreIcon,
                    expandIcon: ChevronRightIcon
                }}
                slotProps={{
                    collapseIcon: { style: { fontSize: 16 } },
                    expandIcon: { style: { fontSize: 16 } }
                }}
                sx={{
                    // Remove default padding on the root list
                    "& > .MuiTreeItem-root": {
                        paddingTop: 0
                    }
                }}
            >
                {renderTree(treeData)}
            </SimpleTreeView>
            <Menu
                open={contextMenu !== null}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
            >
                <MenuItem
                    onClick={() => {
                        const file = contextMenu?.file;
                        setContextMenu(null);
                        if (file?.fileId && file.path) onFileDownload(file.fileId, file.path);
                    }}
                >
                    <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Download file</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default WorkProductEditorFileList;
